import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from '../components/ui/use-toast';
import { FaqAPI, ContentAPI, AdminFaq, ContentKey } from '../lib/api';

type Tab = 'faq' | 'support' | 'privacy' | 'terms' | 'status';

const TABS: { id: Tab; label: string }[] = [
  { id: 'faq', label: 'FAQ' },
  { id: 'support', label: 'Support Contacts' },
  { id: 'privacy', label: 'Privacy Policy' },
  { id: 'terms', label: 'Terms' },
  { id: 'status', label: 'Status Messages' }
];

const ContentManagementPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>('faq');

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Content Management</h1>
        <p className="text-sm text-gray-500">
          Manage the FAQ, support contacts, legal documents and status messages shown in the consumer app.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 border-b pb-2">
        {TABS.map((t) => (
          <Button
            key={t.id}
            variant={tab === t.id ? 'default' : 'outline'}
            size="sm"
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </Button>
        ))}
      </div>

      {tab === 'faq' && <FaqManager />}
      {tab === 'support' && <SupportEditor />}
      {tab === 'privacy' && <LegalEditor contentKey="privacy_policy" title="Privacy Policy" />}
      {tab === 'terms' && <LegalEditor contentKey="terms" title="Terms & Conditions" />}
      {tab === 'status' && <StatusMessagesEditor />}
    </div>
  );
};

// ---------------- FAQ ----------------
const FAQ_CATEGORIES = ['general', 'orders', 'payments', 'delivery', 'products', 'account'];

const FaqManager: React.FC = () => {
  const [faqs, setFaqs] = useState<AdminFaq[]>([]);
  const [loading, setLoading] = useState(true);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [category, setCategory] = useState('general');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await FaqAPI.getAll();
      setFaqs(res.data?.faqs || []);
    } catch (e) {
      toast({ title: 'Failed to load FAQs', description: e instanceof Error ? e.message : '' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const addFaq = async () => {
    if (!question.trim() || !answer.trim()) {
      toast({ title: 'Question and answer are required', description: '' });
      return;
    }
    setSaving(true);
    try {
      await FaqAPI.create({ question: question.trim(), answer: answer.trim(), category });
      setQuestion('');
      setAnswer('');
      setCategory('general');
      toast({ title: 'FAQ added', description: '' });
      await load();
    } catch (e) {
      toast({ title: 'Failed to add FAQ', description: e instanceof Error ? e.message : '' });
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (faq: AdminFaq) => {
    try {
      if (faq.isActive === false) {
        await FaqAPI.update(faq._id, { isActive: true });
      } else {
        await FaqAPI.remove(faq._id);
      }
      await load();
    } catch (e) {
      toast({ title: 'Failed to update FAQ', description: e instanceof Error ? e.message : '' });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Add a FAQ</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Question</Label>
            <Input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="e.g. How do I track my order?" />
          </div>
          <div>
            <Label>Answer</Label>
            <Textarea value={answer} onChange={(e) => setAnswer(e.target.value)} rows={3} placeholder="Answer shown to customers" />
          </div>
          <div>
            <Label>Category</Label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="ml-2 rounded-md border px-2 py-1 text-sm"
            >
              {FAQ_CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <Button onClick={addFaq} disabled={saving}>
            {saving ? 'Saving…' : 'Add FAQ'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Existing FAQs ({faqs.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : faqs.length === 0 ? (
            <p className="text-sm text-gray-500">No FAQs yet.</p>
          ) : (
            <div className="space-y-3">
              {faqs.map((faq) => (
                <div key={faq._id} className="rounded-md border p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-medium">{faq.question}</div>
                      <div className="mt-1 text-sm text-gray-600 whitespace-pre-wrap">{faq.answer}</div>
                      <div className="mt-1 text-xs text-gray-400">
                        {faq.category}{faq.isActive === false ? ' · inactive' : ''}
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => toggleActive(faq)}>
                      {faq.isActive === false ? 'Activate' : 'Deactivate'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ---------------- Support contacts ----------------
const SUPPORT_FIELDS: { key: string; label: string; placeholder: string }[] = [
  { key: 'phone', label: 'Phone (dialer, E.164)', placeholder: '+919876543210' },
  { key: 'phoneDisplay', label: 'Phone (display)', placeholder: '+91 98765-43210' },
  { key: 'email', label: 'Support email', placeholder: 'support@yourdomain.com' },
  { key: 'whatsapp', label: 'WhatsApp (digits only)', placeholder: '919876543210' },
  { key: 'feedbackEmail', label: 'Feedback email', placeholder: 'feedback@yourdomain.com' },
  { key: 'hoursWeekdays', label: 'Hours (Mon-Fri)', placeholder: '9:00 AM - 8:00 PM' },
  { key: 'hoursSaturday', label: 'Hours (Saturday)', placeholder: '10:00 AM - 6:00 PM' },
  { key: 'hoursSunday', label: 'Hours (Sunday)', placeholder: 'Closed' }
];

const SupportEditor: React.FC = () => {
  const [value, setValue] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await ContentAPI.get('support_contacts');
        const v = res.data?.value;
        if (v && typeof v === 'object') setValue(v as Record<string, string>);
      } catch {
        /* ignore */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await ContentAPI.upsert('support_contacts', value);
      toast({ title: 'Support contacts saved', description: '' });
    } catch (e) {
      toast({ title: 'Failed to save', description: e instanceof Error ? e.message : '' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-sm text-gray-500">Loading…</p>;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Support Contacts</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {SUPPORT_FIELDS.map((f) => (
          <div key={f.key}>
            <Label>{f.label}</Label>
            <Input
              value={value[f.key] || ''}
              placeholder={f.placeholder}
              onChange={(e) => setValue((p) => ({ ...p, [f.key]: e.target.value }))}
            />
          </div>
        ))}
        <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
      </CardContent>
    </Card>
  );
};

// ---------------- Legal (privacy / terms) ----------------
const LegalEditor: React.FC<{ contentKey: ContentKey; title: string }> = ({ contentKey, title }) => {
  const [value, setValue] = useState<{ title?: string; lastUpdated?: string; body?: string }>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await ContentAPI.get(contentKey);
        const v = res.data?.value;
        if (v && typeof v === 'object') setValue(v);
      } catch {
        /* ignore */
      } finally {
        setLoading(false);
      }
    })();
  }, [contentKey]);

  const save = async () => {
    setSaving(true);
    try {
      await ContentAPI.upsert(contentKey, value);
      toast({ title: `${title} saved`, description: '' });
    } catch (e) {
      toast({ title: 'Failed to save', description: e instanceof Error ? e.message : '' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-sm text-gray-500">Loading…</p>;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label>Title</Label>
          <Input
            value={value.title || ''}
            placeholder={title}
            onChange={(e) => setValue((p) => ({ ...p, title: e.target.value }))}
          />
        </div>
        <div>
          <Label>Last updated (text)</Label>
          <Input
            value={value.lastUpdated || ''}
            placeholder="e.g. 22 May 2026"
            onChange={(e) => setValue((p) => ({ ...p, lastUpdated: e.target.value }))}
          />
        </div>
        <div>
          <Label>Body</Label>
          <Textarea
            value={value.body || ''}
            rows={16}
            placeholder="Full document text. Leave empty to keep the app's built-in default."
            onChange={(e) => setValue((p) => ({ ...p, body: e.target.value }))}
          />
        </div>
        <p className="text-xs text-gray-400">
          When the body is empty, the app shows its built-in default text instead.
        </p>
        <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
      </CardContent>
    </Card>
  );
};

// ---------------- Status messages ----------------
const StatusMessagesEditor: React.FC = () => {
  const [value, setValue] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await ContentAPI.get('status_messages');
        const v = res.data?.value;
        if (v && typeof v === 'object') setValue(v as Record<string, string>);
      } catch {
        /* ignore */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await ContentAPI.upsert('status_messages', value);
      toast({ title: 'Status messages saved', description: '' });
    } catch (e) {
      toast({ title: 'Failed to save', description: e instanceof Error ? e.message : '' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-sm text-gray-500">Loading…</p>;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Status Messages</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label>Awaiting supplier acceptance</Label>
          <Textarea
            value={value.awaitingAcceptance || ''}
            rows={3}
            placeholder="We've received your order and are confirming it with the best supplier — we'll update you shortly."
            onChange={(e) => setValue((p) => ({ ...p, awaitingAcceptance: e.target.value }))}
          />
        </div>
        <p className="text-xs text-gray-400">
          Shown on a new order before a supplier accepts it. Empty = the app's built-in default.
        </p>
        <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
      </CardContent>
    </Card>
  );
};

export default ContentManagementPage;
